// The client-side ReAct loop (spec-9, ac-8). A single agent⇄tools LangGraph
// StateGraph — NO phase routing, just: ask the model (POST /api/agent/chat, SSE),
// run whatever tool_uses it asked for, loop until it stops asking, then audit.
//
// The server runs ONE assistant completion per /chat call and streams it back; it
// never executes tools. So the *loop* lives here on the client: server tools are
// proxied to POST /api/agent/tool, while UI tools (render_chart, render_choices,
// …) are rendered in the transcript via the injected handlers. Interactive UI
// tools (render_choices, render_action_buttons) PAUSE the loop — the tools node
// awaits the user's response — before the synthesized tool_result lets it resume.
//
// The checkpointer is an in-memory MemorySaver (ac-34): the conversation lives in
// React state for the session only, nothing is persisted to a thread store, so a
// reload starts fresh.

import {
  Annotation,
  StateGraph,
  START,
  END,
  MemorySaver,
} from "@langchain/langgraph";
import { getToken } from "../api/client";

// ── Anthropic-style message/content shapes (the wire format with the server) ──

export type Role = "user" | "assistant";

export interface TextBlock {
  type: "text";
  text: string;
}
export interface ToolUseBlock {
  type: "tool_use";
  id: string;
  name: string;
  input: unknown;
}
export interface ToolResultBlock {
  type: "tool_result";
  tool_use_id: string;
  content: string;
  is_error?: boolean;
}
export type ContentBlock = TextBlock | ToolUseBlock | ToolResultBlock;

export interface AgentMessage {
  role: Role;
  content: string | ContentBlock[];
}

/** What a server tool returns (mirrors the server's ToolResult, content only). */
export interface ToolResult {
  content: string;
  isError?: boolean;
}

// The UI tool names the model may "call". These render client-side and are NEVER
// proxied to /api/agent/tool — matches UI_TOOL_NAMES in the server contract.
export const UI_TOOL_NAMES = [
  "render_chart",
  "render_table",
  "render_callout",
  "render_choices",
  "render_action_buttons",
  "render_steps",
  "render_quote",
  "render_progress",
] as const;
export type UiToolName = (typeof UI_TOOL_NAMES)[number];

// Only these two are interactive — the loop waits on the operator before resuming.
export const INTERACTIVE_UI_TOOLS: ReadonlySet<string> = new Set([
  "render_choices",
  "render_action_buttons",
]);

export function isUiTool(name: string): name is UiToolName {
  return (UI_TOOL_NAMES as readonly string[]).includes(name);
}

// ── Per-turn handlers (the React layer wires these to the transcript) ──

export interface TurnHandlers {
  /** Called with each streamed text delta from the current completion. */
  onText: (delta: string) => void;
  /**
   * Render a UI tool_use in the transcript. Display-only tools resolve
   * immediately (e.g. `{ displayed: true }`); interactive tools resolve once the
   * operator responds, with their choice as the payload.
   */
  renderUiTool: (block: ToolUseBlock) => Promise<unknown>;
  /**
   * Surface a SERVER tool call in the transcript for transparency (the operator
   * sees which SQL/curated/logs tool ran and a preview of the result). Optional.
   */
  onServerTool?: (block: ToolUseBlock, result: ToolResult) => void;
}

// ── Transport (injectable so the graph is testable without the network) ──

export interface AgentTransport {
  chat: (
    turnId: string,
    messages: AgentMessage[],
    onText: (delta: string) => void,
    signal?: AbortSignal,
  ) => Promise<AgentMessage>;
  tool: (
    turnId: string,
    name: string,
    input: unknown,
    signal?: AbortSignal,
  ) => Promise<ToolResult>;
  audit: (
    turnId: string,
    prompt: string,
    answer: string,
    error?: string,
  ) => Promise<void>;
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// POST /api/agent/chat — one streamed completion. Parses the `data: {json}\n\n`
// SSE events: text_delta (stream), message (final content blocks), done, error.
async function streamChat(
  turnId: string,
  messages: AgentMessage[],
  onText: (delta: string) => void,
  signal?: AbortSignal,
): Promise<AgentMessage> {
  const res = await fetch("/api/agent/chat", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ turnId, messages }),
    signal,
  });
  if (!res.ok || !res.body) {
    throw new Error(`agent chat failed (${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let content: ContentBlock[] = [];
  let streamError: string | null = null;

  const handleEvent = (raw: string) => {
    const line = raw.trim();
    if (!line.startsWith("data:")) return;
    const json = line.slice("data:".length).trim();
    if (!json) return;
    let evt: { type: string; text?: string; content?: ContentBlock[]; message?: string };
    try {
      evt = JSON.parse(json);
    } catch {
      return; // ignore unparseable keep-alives
    }
    if (evt.type === "text_delta" && evt.text) {
      onText(evt.text);
    } else if (evt.type === "message" && Array.isArray(evt.content)) {
      content = evt.content;
    } else if (evt.type === "error") {
      streamError = evt.message ?? "agent error";
    }
  };

  // eslint-disable-next-line no-constant-condition
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      handleEvent(buffer.slice(0, idx));
      buffer = buffer.slice(idx + 2);
    }
  }
  if (buffer.trim()) handleEvent(buffer);

  if (streamError) throw new Error(streamError);
  return { role: "assistant", content };
}

async function callServerTool(
  turnId: string,
  name: string,
  input: unknown,
  signal?: AbortSignal,
): Promise<ToolResult> {
  const res = await fetch("/api/agent/tool", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ turnId, name, input }),
    signal,
  });
  if (!res.ok) {
    return { content: `tool ${name} failed (${res.status})`, isError: true };
  }
  const body = (await res.json()) as { result: ToolResult };
  return body.result;
}

async function postAudit(
  turnId: string,
  prompt: string,
  answer: string,
  error?: string,
): Promise<void> {
  await fetch("/api/agent/audit", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ turnId, prompt, answer, ...(error ? { error } : {}) }),
  }).catch(() => {});
}

/** The real, fetch-backed transport used by the app. */
export const httpTransport: AgentTransport = {
  chat: streamChat,
  tool: callServerTool,
  audit: postAudit,
};

// ── Graph state ──

const AgentState = Annotation.Root({
  messages: Annotation<AgentMessage[]>({
    reducer: (a, b) => a.concat(b),
    default: () => [],
  }),
  turnId: Annotation<string>(),
  // The operator's original question for this turn — set at invoke, used for audit.
  prompt: Annotation<string>(),
});

type State = typeof AgentState.State;

interface TurnConfigurable {
  handlers: TurnHandlers;
  transport: AgentTransport;
  // Aborts the in-flight completion / tool call when the operator stops the turn.
  signal?: AbortSignal;
}

function readConfigurable(config: {
  configurable?: Partial<TurnConfigurable>;
}): TurnConfigurable {
  const c = config.configurable;
  if (!c?.handlers || !c?.transport) {
    throw new Error("agent graph: handlers and transport are required in config");
  }
  return { handlers: c.handlers, transport: c.transport, signal: c.signal };
}

function toolUsesOf(message: AgentMessage | undefined): ToolUseBlock[] {
  if (!message || !Array.isArray(message.content)) return [];
  return message.content.filter((b): b is ToolUseBlock => b.type === "tool_use");
}

function assistantText(message: AgentMessage | undefined): string {
  if (!message || !Array.isArray(message.content)) {
    return typeof message?.content === "string" ? message.content : "";
  }
  return message.content
    .filter((b): b is TextBlock => b.type === "text")
    .map((b) => b.text)
    .join("");
}

/** Serialize a UI tool_result payload to Anthropic's string content shape. */
function toToolResultContent(payload: unknown): string {
  return typeof payload === "string" ? payload : JSON.stringify(payload);
}

// ── Nodes ──

// Ask the model: one streamed completion, appended as the assistant message.
async function agentNode(
  state: State,
  config: { configurable?: Partial<TurnConfigurable> },
): Promise<Partial<State>> {
  const { handlers, transport, signal } = readConfigurable(config);
  const message = await transport.chat(
    state.turnId,
    state.messages,
    handlers.onText,
    signal,
  );
  return { messages: [message] };
}

// Run every tool_use the model asked for: server tools → /api/agent/tool; UI
// tools → render (interactive ones pause until the operator responds). The
// tool_result blocks come back as a single user message so the loop can resume.
async function toolsNode(
  state: State,
  config: { configurable?: Partial<TurnConfigurable> },
): Promise<Partial<State>> {
  const { handlers, transport, signal } = readConfigurable(config);
  const toolUses = toolUsesOf(state.messages[state.messages.length - 1]);
  const results: ContentBlock[] = [];

  for (const tu of toolUses) {
    if (isUiTool(tu.name)) {
      // Display-only tools resolve at once; interactive ones await onRespond.
      const payload = await handlers.renderUiTool(tu);
      results.push({
        type: "tool_result",
        tool_use_id: tu.id,
        content: toToolResultContent(payload),
      });
    } else {
      const result = await transport.tool(state.turnId, tu.name, tu.input, signal);
      handlers.onServerTool?.(tu, result);
      results.push({
        type: "tool_result",
        tool_use_id: tu.id,
        content: result.content,
        is_error: result.isError,
      });
    }
  }

  return { messages: [{ role: "user", content: results }] };
}

// Finalize the turn: one audit row with the prompt + the model's final answer.
async function auditNode(
  state: State,
  config: { configurable?: Partial<TurnConfigurable> },
): Promise<Partial<State>> {
  const { transport } = readConfigurable(config);
  const answer = assistantText(state.messages[state.messages.length - 1]);
  await transport.audit(state.turnId, state.prompt, answer);
  return {};
}

// After a completion: more tools to run, or finished → audit then END.
function routeAfterAgent(state: State): "tools" | "audit" {
  return toolUsesOf(state.messages[state.messages.length - 1]).length > 0
    ? "tools"
    : "audit";
}

function compileGraph(checkpointer: MemorySaver) {
  return new StateGraph(AgentState)
    .addNode("agent", agentNode)
    .addNode("tools", toolsNode)
    .addNode("audit", auditNode)
    .addEdge(START, "agent")
    .addConditionalEdges("agent", routeAfterAgent, {
      tools: "tools",
      audit: "audit",
    })
    .addEdge("tools", "agent")
    .addEdge("audit", END)
    .compile({ checkpointer });
}

export type AgentGraph = ReturnType<typeof compileGraph>;

export interface BuiltAgentGraph {
  graph: AgentGraph;
  /** Exposed so tests can assert the in-memory-only posture (ac-34). */
  checkpointer: MemorySaver;
}

/** Build + compile the client ReAct loop with an in-memory checkpointer (ac-34). */
export function createAgentGraph(): BuiltAgentGraph {
  const checkpointer = new MemorySaver();
  return { graph: compileGraph(checkpointer), checkpointer };
}

export interface RunTurnArgs {
  graph: AgentGraph;
  turnId: string;
  prompt: string;
  messages: AgentMessage[];
  handlers: TurnHandlers;
  transport?: AgentTransport;
  // When this fires, the in-flight completion / tool fetch is aborted (Esc / Stop).
  signal?: AbortSignal;
}

/**
 * Run one operator turn through the loop. Returns the full message list (history
 * for the next turn). On failure, audits with the error then rethrows.
 */
export async function runTurn({
  graph,
  turnId,
  prompt,
  messages,
  handlers,
  transport = httpTransport,
  signal,
}: RunTurnArgs): Promise<AgentMessage[]> {
  try {
    const result = (await graph.invoke(
      { messages, turnId, prompt },
      {
        // A fresh thread per turn → MemorySaver holds only this turn's
        // checkpoints, discarded after; nothing is persisted (ac-34).
        configurable: { thread_id: turnId, handlers, transport, signal },
        recursionLimit: 50,
      },
    )) as State;
    return result.messages;
  } catch (err) {
    await transport
      .audit(turnId, prompt, "", err instanceof Error ? err.message : String(err))
      .catch(() => {});
    throw err;
  }
}
